import {computed, ref} from "vue";
import {mutate} from "swrv";
import {cacheKey} from "@/lib/cache";
import useSwr from "@/core/composables/swr/useSwr";
import useOrganizationId from "@/core/composables/useOrganizationId";
import {teamTaskApi} from "@/container";
import {TeamTask, TeamTaskStatus} from "@/core/interfaces/TeamTask";
import {TeamTaskStoreRequest, TeamTaskUpdateRequest} from "@/core/http/resources/TeamTaskResource";

/**
 * The task board of the current organization.
 *
 * The whole board is held in one cache entry: it is bounded by nature, and
 * moving a card has to reorder two columns at once, which a per-column fetch
 * would make needlessly chatty.
 */
const useTeamTasks = () => {
    const organizationId = useOrganizationId();
    const failure = ref<string | null>(null);

    const key = computed(() =>
        organizationId.value ? cacheKey(`organizations/${organizationId.value}/team-tasks`) : null
    );

    const {data, isLoading, error} = useSwr(
        () => key.value,
        () => organizationId.value ? teamTaskApi().index(organizationId.value) : Promise.resolve([])
    );

    const tasks = computed<TeamTask[]>(() => data.value ?? []);

    const archivedKey = computed(() =>
        organizationId.value ? cacheKey(`organizations/${organizationId.value}/team-tasks/archived`) : null
    );

    // Loaded alongside the board rather than on demand: the header shows the
    // archive count before the panel is ever opened.
    const {data: archivedData} = useSwr(
        () => archivedKey.value,
        () => organizationId.value
            ? teamTaskApi().index(organizationId.value, {archived: true})
            : Promise.resolve([])
    );

    const archivedTasks = computed<TeamTask[]>(() => archivedData.value ?? []);

    const writeArchivedCache = (next: TeamTask[]) => {
        if (archivedKey.value) {
            mutate(archivedKey.value, next);
        }
    };

    /**
     * Write a list straight into the cache. The instance `mutate` of swrv does
     * not revalidate here, so the global one with an explicit payload is the
     * only reliable way to refresh a board.
     */
    const writeCache = (next: TeamTask[]) => {
        if (key.value) {
            mutate(key.value, next);
        }
    };

    const replaceInCache = (task: TeamTask) => {
        const next = [...tasks.value];
        const index = next.findIndex(candidate => candidate.id === task.id);

        if (index >= 0) {
            next[index] = task;
        } else {
            next.unshift(task);
        }

        writeCache(next);
    };

    const create = async (request: TeamTaskStoreRequest): Promise<TeamTask | null> => {
        if (!organizationId.value) {
            return null;
        }

        failure.value = null;

        try {
            const created = await teamTaskApi().create(organizationId.value, request);

            writeCache([created, ...tasks.value]);

            return created;
        } catch {
            failure.value = "La tâche n'a pas pu être créée.";

            return null;
        }
    };

    const update = async (task: TeamTask, request: TeamTaskUpdateRequest): Promise<TeamTask | null> => {
        failure.value = null;

        try {
            const updated = await teamTaskApi().update(task.id, request);

            replaceInCache(updated);

            return updated;
        } catch {
            failure.value = "La modification n'a pas pu être enregistrée.";

            return null;
        }
    };

    /**
     * Move a card to another column, showing the move at once and undoing it
     * if the server refuses: a drop left in place would claim a status change
     * that never happened.
     */
    const move = async (task: TeamTask, status: TeamTaskStatus): Promise<boolean> => {
        if (task.status === status) {
            return true;
        }

        // Only this card is remembered, never the whole list: restoring a
        // snapshot would also undo any other move that landed while this one
        // was in flight.
        const rollback = tasks.value.find(candidate => candidate.id === task.id) ?? task;

        failure.value = null;
        replaceInCache({...task, status});

        try {
            const updated = await teamTaskApi().update(task.id, {status});

            replaceInCache(updated);

            return true;
        } catch {
            replaceInCache(rollback);
            failure.value = `« ${task.title} » n'a pas pu être déplacée : la carte est revenue dans sa colonne.`;

            return false;
        }
    };

    /**
     * Set the order of one column, and move a card into it when the list names
     * one that sat elsewhere.
     *
     * The whole column is sent rather than a task and an index: the server
     * then rewrites its ranks in one pass, so no two cards can end up sharing
     * one, and the outcome does not depend on what the board thought the ranks
     * were when the drag started.
     */
    const reorder = async (status: TeamTaskStatus, taskIds: number[]): Promise<boolean> => {
        if (!organizationId.value) {
            return false;
        }

        const rollback = tasks.value;
        const byId = new Map(rollback.map(task => [task.id, task]));

        failure.value = null;

        const moved = taskIds
            .map((id, position) => {
                const task = byId.get(id);

                return task ? {...task, status, position} : null;
            })
            .filter((task): task is TeamTask => !!task);

        const movedIds = new Set(moved.map(task => task.id));

        writeCache([
            ...rollback.filter(task => !movedIds.has(task.id) && task.status !== status),
            ...moved
        ]);

        try {
            const column = await teamTaskApi().reorder(organizationId.value, status, taskIds);
            const columnIds = new Set(column.map(task => task.id));

            writeCache([
                ...tasks.value.filter(task => !columnIds.has(task.id) && task.status !== status),
                ...column
            ]);

            return true;
        } catch {
            writeCache(rollback);
            failure.value = "Le nouvel ordre n'a pas pu être enregistré : les cartes ont repris leur place.";

            return false;
        }
    };

    /**
     * Archive every finished task. Both lists are written from what the call
     * returns rather than refetched: the board loses exactly the finished
     * cards, and the panel gains them.
     */
    const archiveCompleted = async (): Promise<number | null> => {
        if (!organizationId.value) {
            return null;
        }

        failure.value = null;

        try {
            const count = await teamTaskApi().archiveCompleted(organizationId.value);
            const archived = tasks.value.filter(task => task.status === 'done');

            writeCache(tasks.value.filter(task => task.status !== 'done'));
            writeArchivedCache([...archived, ...archivedTasks.value]);

            return count;
        } catch {
            failure.value = "Les tâches terminées n'ont pas pu être archivées.";

            return null;
        }
    };

    /** Put an archived task back on the board, in the column it left. */
    const restore = async (task: TeamTask): Promise<boolean> => {
        failure.value = null;

        try {
            const restored = await teamTaskApi().restore(task.id);

            writeArchivedCache(archivedTasks.value.filter(candidate => candidate.id !== task.id));
            writeCache([restored, ...tasks.value]);

            return true;
        } catch {
            failure.value = `« ${task.title} » n'a pas pu être restaurée.`;

            return false;
        }
    };

    /**
     * Reflect a freshly posted comment on the card counter.
     *
     * Read from the cache by id rather than from a task handed in: the dialog
     * holds the snapshot it opened with, so several comments in a row would
     * all increment the same stale count.
     */
    const countComment = (taskId: number) => {
        const current = tasks.value.find(task => task.id === taskId);

        if (current) {
            replaceInCache({...current, comments_count: current.comments_count + 1});
        }
    };

    return {
        tasks,
        archivedTasks,
        loading: isLoading,
        loadFailed: computed(() => !!error.value),
        failure,
        clearFailure: () => {
            failure.value = null;
        },
        create,
        update,
        move,
        reorder,
        archiveCompleted,
        restore,
        countComment
    };
};

export default useTeamTasks;
